import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, AbstractControl, Validators } from '@angular/forms';
import { InnerPackagingModel, CompositePackagingModel, EnumPackageType, EasyDgdService } from '@hazmatsuite-next/easydgd-lib';
import { DangerousGoodItem, LanguageService, TransportMode, minValidator } from '@hazmatsuite-next/ui';
import { UntilDestroy } from '@ngneat/until-destroy';
import { debounceTime, map, Observable, take } from 'rxjs';


@UntilDestroy()
@Component({
  selector: 'easydgd-inner-packaging-item-editor',
  templateUrl: './inner-packaging-item-editor.component.html',
  styleUrls: ['./inner-packaging-item-editor.component.scss'],
})
export class InnerPackagingItemEditorComponent {
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Input() set model(m: InnerPackagingModel) {
    this.innerPackagingModel = m;

    this.dangerousGood = this.innerPackagingModel?.DangerousGood
    this.initForm();
  };
  @Output() valid = new EventEmitter<boolean>();
  @Output() modelChanged = new EventEmitter<InnerPackagingModel>();

  innerPackagingModel?: InnerPackagingModel;
  dangerousGood?: DangerousGoodItem;
  formGroup: FormGroup = new FormGroup({});
  compositePackaging?: CompositePackagingModel;

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource']));

  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  constructor(
    private builder: FormBuilder,
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService,
    private cd: ChangeDetectorRef
  ) { }

  public selectDangerousGood(dangerousGood: DangerousGoodItem): void {
    this.dangerousGood = dangerousGood;
    this.properShippingName?.setValue(dangerousGood?.DefaultShippingExpression, { onlySelf: true });
    this.easyDgdService.initializeTempPackage(dangerousGood.ID, EnumPackageType.InnerPacking, this.easyDgdService.getTransportMode()).pipe(
      take(1)
    ).subscribe(pkg => {
      this.innerPackagingModel = pkg as InnerPackagingModel;
      this.innerPackagingModel.DangerousGood = this.dangerousGood;

      this.initForm();
    });
  }

  public changeIsEnvironmentallyHazardous(evt: any): void {
    this.isEnvironmentallyHazardous?.setValue(evt.currentTarget.checked, { onlySelf: true });
  }

  public markAsTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  private initForm() {
    this.formGroup = this.builder.group({
      properShippingName: new FormControl({ value: '', disabled: true }),
      isEnvironmentallyHazardous: new FormControl({ value: false, disabled: this.innerPackagingModel?.IsEnvironmentallyHazardousFromDb ?? false }),
      lowerFlashpoint: new FormControl('', (ctrl: AbstractControl) =>
        this.innerPackagingModel?.IsFlashpointRequired ? Validators.required(ctrl) : null),
      countOfPackagings: new FormControl(1, [Validators.required, Validators.min(1)]),
      alternativePackagingType: new FormControl('', (ctrl: AbstractControl) => Validators.required(ctrl)),
      isTotalQuantity: new FormControl(false, Validators.required),
      netWeight: new FormControl(null, [(ctrl: AbstractControl) =>
        this.innerPackagingModel?.IsNetWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.innerPackagingModel?.IsNetWeightMandatory ? minValidator()(ctrl) : null]
      ),
      netVolume: new FormControl(null, [(ctrl: AbstractControl) =>
        this.innerPackagingModel?.IsNetVolumeMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.innerPackagingModel?.IsNetVolumeMandatory ? minValidator()(ctrl) : null]
      ),
      grossWeight: new FormControl(null, [(ctrl: AbstractControl) =>
        this.innerPackagingModel?.IsGrossWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.innerPackagingModel?.IsGrossWeightMandatory ? minValidator()(ctrl) : null]
      ),
      netExplosiveMass: new FormControl(null, [(ctrl: AbstractControl) =>
        this.innerPackagingModel?.IsNetExplosiveMassMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.innerPackagingModel?.IsNetExplosiveMassMandatory ? minValidator()(ctrl) : null]
      ),
    });

    this.formGroup.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      this.valid.emit(this.formGroup.valid);
      this.modelChanged.emit({
        ...this.innerPackagingModel as InnerPackagingModel,
        DangerousGood: this.dangerousGood,
        DangerousGoodId: this.dangerousGood?.ID,
        IsEnvironmentallyHazardous: value.isEnvironmentallyHazardous,
        NetWeightValue: value.netWeight,
        NetWeightUnitSymbol: this.innerPackagingModel?.NetWeightUnitSymbol ?? '',
        NetVolumeValue: value.netVolume,
        NetVolumeUnitSymbol: this.innerPackagingModel?.NetVolumeUnitSymbol ?? '',
        GrossWeightValue: value.grossWeight,
        GrossWeightUnitSymbol: this.innerPackagingModel?.GrossWeightUnitSymbol ?? '',
        NetExplosiveMassValue: value.netExplosiveMass,
        NetExplosiveMassUnitSymbol: this.innerPackagingModel?.NetExplosiveMassUnitSymbol ?? '',
        CountOfPackagings: value.countOfPackagings,
        AlternativePackagingType: value.alternativePackagingType,
        IsTotalQuantity: JSON.parse(value.isTotalQuantity),
        InternalId: this.innerPackagingModel?.InternalId ?? '',
      });
    });

    this.isEnvironmentallyHazardous?.setValue(this.innerPackagingModel?.IsEnvironmentallyHazardous || this.innerPackagingModel?.IsEnvironmentallyHazardousFromDb, { onlySelf: true });
    this.countOfPackagings?.setValue(this.innerPackagingModel?.CountOfPackagings === 0 ? 1 : this.innerPackagingModel?.CountOfPackagings, { onlySelf: true });
    this.packagingUnCode?.setValue(this.innerPackagingModel?.PackagingUnCode, { onlySelf: true });
    this.alternativePackagingType?.setValue(this.innerPackagingModel?.AlternativePackagingType, { onlySelf: true });
    this.netWeight?.setValue(this.innerPackagingModel?.NetWeightValue, { onlySelf: true });
    this.netVolume?.setValue(this.innerPackagingModel?.NetVolumeValue, { onlySelf: true });
    this.grossWeight?.setValue(this.innerPackagingModel?.GrossWeightValue, { onlySelf: true });
    this.netExplosiveMass?.setValue(this.innerPackagingModel?.NetExplosiveMassValue, { onlySelf: true });
    this.isTotalQuantity?.setValue((this.innerPackagingModel?.IsTotalQuantity || false), { onlySelf: true });
    if (!this.innerPackagingModel?.IsEnvironmentallyHazardousEnabled || this.innerPackagingModel?.IsEnvironmentallyHazardousFromDb) {
      this.isEnvironmentallyHazardous?.disable();
    }

    this.properShippingName?.setValue(this.innerPackagingModel?.DangerousGood?.DefaultShippingExpression, { onlySelf: true });

    this.cd.markForCheck();
  }

  get properShippingName(): AbstractControl | null {
    return this.formGroup.get('properShippingName');
  }
  get isEnvironmentallyHazardous(): AbstractControl | null {
    return this.formGroup?.get('isEnvironmentallyHazardous');
  }
  get lowerFlashpoint(): AbstractControl | null {
    return this.formGroup?.get('lowerFlashpoint');
  }
  get countOfPackagings(): AbstractControl | null {
    return this.formGroup?.get('countOfPackagings');
  }
  get packagingUnCode(): AbstractControl | null {
    return this.formGroup?.get('packagingUnCode');
  }
  get alternativePackagingType(): AbstractControl | null {
    return this.formGroup?.get('alternativePackagingType');
  }
  get netWeight(): AbstractControl | null {
    return this.formGroup?.get('netWeight');
  }
  get netVolume(): AbstractControl | null {
    return this.formGroup?.get('netVolume');
  }
  get grossWeight(): AbstractControl | null {
    return this.formGroup?.get('grossWeight');
  }
  get netExplosiveMass(): AbstractControl | null {
    return this.formGroup?.get('netExplosiveMass');
  }
  get isTotalQuantity(): AbstractControl | null {
    return this.formGroup?.get('isTotalQuantity');
  }
}
